import { useState, useContext, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import Image from 'next/image'
import LanguageSwitcher from '../components/LanguageSwitcher'
import SVGLogo from '../components/SVGLogo'
import { login as performLogin } from '../hooks/useAuth'
import { AuthContext } from '../context/AuthContext'
import { LanguageProvider, useLanguage } from '../context/LanguageContext'
import AUTH_COPY from '../locales/auth'


// Seed the language from Cloudflare's CF-IPCountry header via /v1/geo/.
// Client-side localStorage override still wins on subsequent renders.
export async function getServerSideProps(context) {
  const INTERNAL_API = process.env.INTERNAL_API_URL || 'http://backend:8000'
  let suggestedLanguage = 'en'
  try {
    const cfCountry = context.req.headers['cf-ipcountry'] || ''
    const res = await fetch(`${INTERNAL_API}/v1/geo/`, {
      headers: { 'CF-IPCountry': cfCountry },
    })
    if (res.ok) {
      const data = await res.json()
      if (['en', 'ht', 'fr', 'es', 'pt-BR', 'zh-Hans'].includes(data.suggested_language)) {
        suggestedLanguage = data.suggested_language
      }
    }
  } catch (_) { /* geo hint is best-effort */ }
  return { props: { suggestedLanguage } }
}


export default function LoginPage({ suggestedLanguage }) {
  return (
    <LanguageProvider initialSuggestion={suggestedLanguage}>
      <Login />
    </LanguageProvider>
  )
}


function Login() {
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isAutoFilling, setIsAutoFilling] = useState(false)
  const [hasAttemptedAutoLogin, setHasAttemptedAutoLogin] = useState(false)
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false)
  const router = useRouter()
  const { language } = useLanguage()
  const t = AUTH_COPY[language]?.login || AUTH_COPY.en.login
  const { login: updateAuthContext } = useContext(AuthContext)
  const autoLoginTimeoutRef = useRef(null)
  const formRef = useRef(null)

  // Surface OAuth error query params (e.g. Facebook didn't share email)
  // and the session-expired redirect from services/api.js.
  useEffect(() => {
    if (!router.isReady) return
    const { error: errCode, provider, message, session_expired } = router.query

    // JWT session-expiration path. Two signals converge here:
    //   1. ?session_expired=1 from services/api.js's forceSignoutAndRedirect
    //      (refresh token itself 401'd on the axios interceptor path).
    //   2. sessionStorage.session_just_expired stamped by hooks/useAuth.js
    //      when isAuthenticated() / getUsernameFromToken() detect a stored
    //      token that just expired or won't parse. Covers all ~20 pages
    //      that call `router.push('/login?redirect=...')` after seeing
    //      isAuthenticated=false, so the banner fires regardless of which
    //      route-guard path did the redirect.
    // Read once and clear so a subsequent navigation doesn't re-fire it.
    let sessionExpiredFlag = false
    if (typeof window !== 'undefined') {
      try {
        if (sessionStorage.getItem('session_just_expired') === '1') {
          sessionExpiredFlag = true
          sessionStorage.removeItem('session_just_expired')
        }
      } catch (_) { /* no-op */ }
    }
    if (session_expired === '1' || sessionExpiredFlag) {
      setError(t.err_session_expired)
      return
    }

    if (!errCode) return
    const providerLabel = provider === 'facebook' ? 'Facebook'
      : provider === 'google' ? 'Google'
      : provider === 'github' ? 'GitHub'
      : provider === 'apple' ? 'Apple'
      : t.provider_default
    if (errCode === 'no_email') {
      setError(`${providerLabel}${t.err_no_email_a}${providerLabel}${t.err_no_email_b}`)
    } else if (errCode === 'oauth_cancelled') {
      setError(`${t.err_cancelled_a}${providerLabel}${t.err_cancelled_b}`)
    } else if (errCode === 'invalid_state' || errCode === 'missing_state') {
      setError(`${t.err_state_a}${providerLabel}${t.err_state_b}`)
    } else if (errCode === 'oauth_failed') {
      setError(
        message
          ? `${providerLabel}${t.err_oauth_failed_a}${t.err_oauth_failed_message}${message}${t.err_oauth_failed_z}`
          : `${providerLabel}${t.err_oauth_failed_a}${t.err_oauth_failed_z}`
      )
    }
  }, [router.isReady, router.query, t])

  // Auto-fill detection using multiple strategies
  useEffect(() => {
    if (!formRef.current) return

    // Strategy 1: Check for immediate auto-fill on page load
    const checkInitialAutoFill = () => {
      const usernameInput = formRef.current.querySelector('input[name="username"]')
      const passwordInput = formRef.current.querySelector('input[name="password"]')
      
      if (usernameInput?.value && passwordInput?.value) {
        setIdentifier(usernameInput.value)
        setPassword(passwordInput.value)
        setIsAutoFilling(true)
      }
    }

    // Check after a short delay to catch password managers that fill slightly after page load
    const initialCheckTimeout = setTimeout(checkInitialAutoFill, 100)

    // Strategy 2: Listen for animation events (Chrome/Edge)
    const handleAnimationStart = (e) => {
      if (e.animationName === 'onAutoFillStart') {
        setIsAutoFilling(true)
      }
    }

    // Strategy 3: Listen for input events that happen without user interaction
    const handleInput = (e) => {
      // Check if both fields have values and we haven't tried auto-login yet
      if (identifier && password && !hasAttemptedAutoLogin) {
        // Clear any existing timeout
        if (autoLoginTimeoutRef.current) {
          clearTimeout(autoLoginTimeoutRef.current)
        }
        
        // Set a new timeout to attempt login after 500ms of no changes
        autoLoginTimeoutRef.current = setTimeout(() => {
          if (!hasAttemptedAutoLogin) {
            handleAutoLogin()
          }
        }, 500)
      }
    }

    // Add event listeners
    formRef.current.addEventListener('animationstart', handleAnimationStart, true)
    formRef.current.addEventListener('input', handleInput, true)

    return () => {
      clearTimeout(initialCheckTimeout)
      if (autoLoginTimeoutRef.current) {
        clearTimeout(autoLoginTimeoutRef.current)
      }
      if (formRef.current) {
        formRef.current.removeEventListener('animationstart', handleAnimationStart, true)
        formRef.current.removeEventListener('input', handleInput, true)
      }
    }
  }, [])

  // Watch for when both fields are filled
  useEffect(() => {
    if (identifier && password && !hasAttemptedAutoLogin && isAutoFilling) {
      // Debounce the auto-login attempt
      if (autoLoginTimeoutRef.current) {
        clearTimeout(autoLoginTimeoutRef.current)
      }
      
      autoLoginTimeoutRef.current = setTimeout(() => {
        handleAutoLogin()
      }, 300)
    }
  }, [identifier, password, hasAttemptedAutoLogin, isAutoFilling])

  const handleAutoLogin = async () => {
    if (hasAttemptedAutoLogin) return
    
    setHasAttemptedAutoLogin(true)
    setIsAutoLoggingIn(true)
    setError('') // Clear any previous errors
    
    try {
      const result = await performLogin(identifier, password)
      if (result.success) {
        updateAuthContext(identifier)
        setTimeout(() => {
          const destination = router.query.redirect || '/'
          router.replace(destination)
        }, 100)
      } else {
        if (result.error === 'email_not_verified') {
          setError(result.message)
        } else {
          setError(result.message || t.err_invalid_credentials)
        }
        setIsAutoLoggingIn(false)
      }
    } catch (error) {
      console.error('Auto-login error:', error)
      setError(t.err_login_failed)
      setIsAutoLoggingIn(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setHasAttemptedAutoLogin(true) // Prevent auto-login after manual submit
    setError('') // Clear any previous errors
    
    try {
      const result = await performLogin(identifier, password)
      if (result.success) {
        // Update the auth context to trigger re-renders
        updateAuthContext(identifier)
        
        // Small delay to ensure state updates propagate
        setTimeout(() => {
          // Use router.replace instead of push to avoid navigation history issues
          // and check for destination in the query params, falling back to the homepage
          const destination = router.query.redirect || '/'
          router.replace(destination)
        }, 100)
      } else {
        if (result.error === 'email_not_verified') {
          setError(
            <div>
              <p>{result.message}</p>
              <Link href="/resend-verification">
                <a className="text-blue-600 dark:text-blue-400 hover:underline mt-2 inline-block">
                  {t.err_resend_link}
                </a>
              </Link>
            </div>
          )
        } else {
          setError(result.message || t.err_invalid_credentials)
        }
      }
    } catch (error) {
      console.error('Login error:', error)
      setError(t.err_login_failed)
    }
  }

  return (
    <>
      <style jsx>{`
        /* Detect Chrome auto-fill */
        input:-webkit-autofill {
          animation-name: onAutoFillStart;
        }
        @keyframes onAutoFillStart {
          from {}
          to {}
        }
      `}</style>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg py-4 px-3 overscroll-behavior-none relative">
      <LanguageSwitcher />
      <div className="w-full max-w-md bg-white dark:bg-dark-card rounded-lg shadow-md p-4 sm:p-6 touch-action-manipulation">
        <div className="flex justify-center mb-6">
          <SVGLogo
            src="/CreoleCentric-Logo-v6-normal-blue.svg"
            alt="CreoleCentric"
            width={300}
            height={300}
            className="h-32 sm:h-48 w-auto"
          />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-center mb-6 text-gray-900 dark:text-dark-text">{t.h2}</h2>

        <div className="space-y-3 mb-6">
          <button 
            onClick={() => window.location.href = `/v1/users/oauth/login/google/?next=${encodeURIComponent(router.query.redirect || '/')}`}
            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-4 py-3 sm:py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 flex items-center justify-center gap-2 min-h-[44px] touch-action-manipulation"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t.cta_google}
          </button>
          <button 
            onClick={() => window.location.href = `/v1/users/oauth/login/facebook/?next=${encodeURIComponent(router.query.redirect || '/')}`}
            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-4 py-3 sm:py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 flex items-center justify-center gap-2 min-h-[44px] touch-action-manipulation"
          >
            <svg className="w-5 h-5" fill="#1877F2" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            {t.cta_facebook}
          </button>
          <button 
            onClick={() => window.location.href = `/v1/users/oauth/login/github/?next=${encodeURIComponent(router.query.redirect || '/')}`}
            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-4 py-3 sm:py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 flex items-center justify-center gap-2 min-h-[44px] touch-action-manipulation"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            {t.cta_github}
          </button>
          <button 
            onClick={() => window.location.href = `/v1/users/oauth/login/apple/?next=${encodeURIComponent(router.query.redirect || '/')}`}
            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-4 py-3 sm:py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 flex items-center justify-center gap-2 min-h-[44px] touch-action-manipulation"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            {t.cta_apple}
          </button>
          {/* SSO Login - Disabled for now */}
          {/* <button 
            onClick={() => router.push('/sso-login')}
            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            Sign in with SSO
          </button> */}
        </div>

        <div className="flex items-center mb-6">
          <div className="flex-grow h-px bg-gray-300 dark:bg-gray-600" />
          <span className="mx-3 text-sm text-gray-500 dark:text-gray-400">{t.divider}</span>
          <div className="flex-grow h-px bg-gray-300 dark:bg-gray-600" />
        </div>

        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
          <input
            type="text"
            name="username"
            placeholder={t.placeholder_identifier}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 text-base sm:text-sm min-h-[44px]"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck="false"
            autoCorrect="off"
            required
          />
          <div>
            <input
              type="password"
              name="password"
              placeholder={t.placeholder_password}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 text-base sm:text-sm min-h-[44px]"
              autoComplete="current-password"
              required
            />
            <div className="text-right mt-1">
              <a href="/forgot-password" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                {t.forgot_password}
              </a>
            </div>
          </div>
          {error && <p className="text-red-500 dark:text-red-400 text-sm">{error}</p>}
          {isAutoLoggingIn && (
            <div className="text-center text-sm text-blue-600 dark:text-blue-400 py-2">
              <div className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{t.auto_signing_in}</span>
              </div>
            </div>
          )}
          <button 
            type="submit" 
            className="w-full bg-blue-600 dark:bg-blue-700 text-white py-3 sm:py-2 rounded hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-action-manipulation"
            disabled={isAutoLoggingIn}
          >
            {isAutoLoggingIn ? t.submit_signing : t.submit_idle}
          </button>
        </form>

        <div className="text-center mt-6 text-sm text-gray-600 dark:text-gray-400">
          {t.no_account_prefix}
          <Link href="/signup" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
            {t.no_account_link}
          </Link>
        </div>
      </div>
    </div>
    </>
  )
}
